import React, { useMemo } from 'react'
import { DcChart, DcDiv, DcFlexBox, DcText, colors } from 'pixel-base'
import { InventoryProduct } from './types'

interface SellingSlowerChartProps {
  data: InventoryProduct[]
  loading: boolean
  error: string | null
  onProductClick?: (product: InventoryProduct) => void
  showScatterPlotOnly?: boolean
  onTitleClick?: () => void
}

const SellingSlowerChart: React.FC<SellingSlowerChartProps> = ({
  data,
  loading,
  error,
  onProductClick,
  showScatterPlotOnly = false,
  onTitleClick,
}) => {
  // Transform data for warning cards
  const warningCards = useMemo(() => {
    if (!data || data.length === 0) {
      return []
    }

    return data.slice(0, 9).map((product) => {
      let warningType: 'critical' | 'warning' | 'attention' = 'attention'
      let warningColor = colors.warning

      const velocity = product.daily_velocity || 0
      const daysSinceUpdate = product.days_since_update || 0

      if (velocity < 0.5 || daysSinceUpdate > 60) {
        warningType = 'critical'
        warningColor = colors.danger
      } else if (velocity < 1 || daysSinceUpdate > 30) {
        warningType = 'warning'
        warningColor = colors.warning
      }

      return {
        ...product,
        warningType,
        warningColor,
        velocity,
        daysSinceUpdate,
      }
    })
  }, [data])

  // Transform data for scatter plot (inventory value vs sales velocity)
  const scatterData = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        series: [
          {
            name: 'Value vs Velocity',
            type: 'scatter',
            data: [],
            symbolSize: (data: number[]) => Math.sqrt(data[2]) * 2,
          },
        ],
      }
    }

    const scatterPoints = data.map((product) => [
      product.inventory_value || 0,
      product.daily_velocity || 0,
      product.days_since_update || 0, // Used for bubble size
      product.product_name,
    ])

    return {
      series: [
        {
          name: 'Value vs Velocity',
          type: 'scatter',
          data: scatterPoints,
          symbolSize: (data: number[]) =>
            Math.max(5, Math.min(30, Math.sqrt(data[2]) * 2)),
          itemStyle: {
            color: (params: any) => {
              const daysSinceUpdate = params.data[2]
              if (daysSinceUpdate > 60) return colors.danger
              if (daysSinceUpdate > 30) return colors.warning
              return colors.info
            },
            opacity: 0.7,
          },
        },
      ],
    }
  }, [data])

  const scatterOptions = useMemo(
    () => ({
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => `
          <div style="padding: 8px;">
            <strong>${params.data[3]}</strong><br/>
            Inventory Value: $${params.data[0].toLocaleString()}<br/>
            Daily Velocity: ${params.data[1].toFixed(2)}<br/>
            Days Since Update: ${params.data[2]}
          </div>
        `,
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
      },
      xAxis: {
        type: 'value',
        name: 'Inventory Value ($)',
        axisLabel: {
          formatter: (value: number) => `$${(value / 1000).toFixed(0)}k`,
        },
      },
      yAxis: {
        type: 'value',
        name: 'Daily Velocity',
        axisLabel: {
          formatter: '{value}',
        },
      },
      series: scatterData.series,
    }),
    [scatterData],
  )

  if (loading) {
    return (
      <DcDiv
        style={{
          height: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <DcText>Loading inventory data...</DcText>
      </DcDiv>
    )
  }

  if (error) {
    return (
      <DcDiv
        style={{
          height: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <DcText color={colors.danger}>Error loading data: {error}</DcText>
      </DcDiv>
    )
  }

  // Show only scatter plot
  if (showScatterPlotOnly) {
    return (
      <DcDiv>
        <DcChart
          type="scatter"
          data={scatterData}
          options={scatterOptions}
          height={300}
          width="100%"
          isLoading={loading}
        />
      </DcDiv>
    )
  }

  // Show both (original layout)
  return (
    <DcDiv>
      <DcFlexBox direction="column" gap="20px" height="auto">
        {/* Primary Chart - Warning Cards Grid */}
        <DcDiv>
          <DcText
            style={{
              fontSize: '18px',
              fontWeight: 'bold',
              marginBottom: '16px',
              color: colors.textPrimary,
              cursor: onTitleClick ? 'pointer' : 'default',
            }}
            onClick={onTitleClick}
          >
            Slow-Moving Inventory Alerts
          </DcText>
          <DcDiv
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '16px',
            }}
          >
            {warningCards.map((product, index) => (
              <DcDiv
                key={product.product_id}
                style={{
                  padding: '16px',
                  borderRadius: '8px',
                  backgroundColor: product.warningColor,
                  color: 'white',
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease',
                  border: `2px solid ${product.warningColor}`,
                }}
                hoverStyle={{
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
                onClick={() => onProductClick?.(product)}
              >
                <DcText
                  style={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                  }}
                >
                  {product.velocity.toFixed(2)}
                </DcText>
                <DcText
                  style={{
                    fontSize: '12px',
                    marginBottom: '4px',
                    opacity: 0.9,
                  }}
                >
                  {product.product_name.length > 20
                    ? product.product_name.substring(0, 20) + '...'
                    : product.product_name}
                </DcText>
                <DcText
                  style={{
                    fontSize: '10px',
                    opacity: 0.8,
                    marginBottom: '4px',
                  }}
                >
                  {product.daysSinceUpdate} days old
                </DcText>
                <DcText
                  style={{
                    fontSize: '10px',
                    opacity: 0.8,
                    textTransform: 'uppercase',
                    fontWeight: 'bold',
                  }}
                >
                  {product.warningType}
                </DcText>
              </DcDiv>
            ))}
          </DcDiv>
        </DcDiv>

        {/* Secondary Chart - Scatter Plot */}
        <DcDiv>
          <DcText
            style={{
              fontSize: '18px',
              fontWeight: 'bold',
              marginBottom: '16px',
              color: colors.textPrimary,
            }}
          >
            Inventory Value vs Sales Velocity
          </DcText>
          <DcText
            style={{
              fontSize: '12px',
              color: colors.textSecondary,
              marginBottom: '12px',
            }}
          >
            Bubble size represents days since last update
          </DcText>
          <DcChart
            type="scatter"
            data={scatterData}
            options={scatterOptions}
            height={300}
            width="100%"
            isLoading={loading}
            onClick={(params: any) => {
              if (onProductClick && data[params.dataIndex]) {
                onProductClick(data[params.dataIndex])
              }
            }}
          />
        </DcDiv>
      </DcFlexBox>
    </DcDiv>
  )
}

export default SellingSlowerChart
